import {Button} from "primereact/button";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {useState} from "react";

import {IconFieldLabel} from "../component/iconFieldChildren";
import {useRevaTranslation} from "../hooks/languageHooks";
import {useNewPasswordFields} from "../hooks/useNewPasswordFields";
import {redeemPasswordResetThunk} from "../redux/passwordResetThunk";
import {useRevaDispatch} from "../redux/revaHooks";
import {resolveUserApiErrorMessage} from "../util/errorUtil";
import {handlePreventDefault} from "../util/events";
import LoginCardHeader, {LOGIN_CARD_CLASS} from "./LoginCardHeader";
import GeneratedPasswordDisplay from "./widgets/GeneratedPasswordDisplay";

/**
 * #19351: Standalone page for the "reset password" link sent by e-mail (`/reset-password?token=…`).
 * Rendered directly by {@link AppContainer} — deliberately OUTSIDE the login-gated `RouterProvider` (that
 * router is only built once a user is logged in, see `routerFactory`), so it stays reachable regardless of
 * login state and is never hidden behind the login form. Consequently it reads the token straight from
 * `window.location` instead of a router hook (no Router context is guaranteed here), and "back to login"
 * is a full navigation (`window.location.assign`), not a client-side route change. Looks like the login card
 * ({@link LoginCardHeader}, same float-label fields), since users arrive here instead of the login.
 */
export default function ResetPasswordPage() {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();

    const [token] = useState(() => new URLSearchParams(window.location.search).get("token"));

    // The link only goes to confirmed addresses, so a mistyped password is recoverable: no repeat field (#19351).
    const newPw = useNewPasswordFields(false);
    const {newPassword} = newPw;
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const onSubmit = async () => {
        if (!token || !newPassword || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            await dispatch(redeemPasswordResetThunk(token, newPassword));
            // The token is now used up (one-time link) — scrub it from the URL right away so it cannot
            // linger in the browser history/a bookmark.
            window.history.replaceState(null, "", window.location.pathname);
            setSuccess(true);
        } catch (e) {
            setError(
                resolveUserApiErrorMessage(
                    e,
                    language,
                    t("passwordReset.page.unavailable"),
                    t("general.error.unspecific"),
                ),
            );
        } finally {
            setSubmitting(false);
        }
    };

    const goToLogin = () => window.location.assign("/");

    const passwordField = (id: string, labelKey: string, value: string, onChange: (value: string) => void) => (
        <div className="field col-span-12 mt-4">
            <IconField iconPosition="left" className="p-float-label">
                <InputIcon className="pi pi-lock" />
                <InputText
                    id={id}
                    value={value}
                    className="w-full"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    disabled={submitting}
                    onChange={(e) => onChange(e.target.value)}
                />
                <IconFieldLabel htmlFor={id} className="block font-medium">
                    {t(labelKey)}
                </IconFieldLabel>
            </IconField>
        </div>
    );

    return (
        <div className="grow">
            <div className="flex items-center justify-center h-full">
                <form
                    className={`ResetPasswordPage ${LOGIN_CARD_CLASS}`}
                    onSubmit={handlePreventDefault(() => void onSubmit())}
                >
                    <LoginCardHeader subtitle={t("passwordReset.page.header")} />

                    {!token && (
                        <Message
                            id="resetPasswordMissingToken"
                            severity="warn"
                            text={t("passwordReset.page.missingToken")}
                            className="w-full"
                        />
                    )}

                    {token && success && (
                        <div className="flex flex-col gap-4">
                            <Message
                                id="resetPasswordSuccess"
                                severity="success"
                                text={t("passwordReset.page.success")}
                                className="w-full"
                            />
                            <Button
                                id="resetPasswordBackToLoginBtn"
                                type="button"
                                className="w-full"
                                icon="pi pi-user"
                                label={t("passwordReset.page.backToLogin")}
                                onClick={goToLogin}
                            />
                        </div>
                    )}

                    {token && !success && (
                        <div className="grid grid-cols-12 formgrid">
                            {passwordField(
                                "resetPasswordNewPassword",
                                "passwordReset.page.newPassword",
                                newPassword,
                                newPw.changeNewPassword,
                            )}
                            <div className="field col-span-12 mt-2 flex items-start justify-between gap-2">
                                <Button
                                    id="resetPasswordShowBtn"
                                    type="button"
                                    link
                                    size="small"
                                    className="!p-0 shrink-0"
                                    icon={showPassword ? "pi pi-eye-slash" : "pi pi-eye"}
                                    label={t(
                                        showPassword
                                            ? "passwordReset.page.hidePassword"
                                            : "passwordReset.page.showPassword",
                                    )}
                                    onClick={() => setShowPassword((show) => !show)}
                                />
                                <Button
                                    id="resetPasswordGenerateBtn"
                                    type="button"
                                    link
                                    size="small"
                                    className="!p-0 shrink-0"
                                    icon="pi pi-refresh"
                                    label={t("passwordReset.page.generate")}
                                    disabled={submitting}
                                    onClick={() => newPw.generatePassword()}
                                />
                            </div>
                            {newPw.generatedPassword && (
                                <div className="field col-span-12">
                                    <GeneratedPasswordDisplay
                                        idPrefix="resetPassword"
                                        password={newPw.generatedPassword}
                                        copied={newPw.copied}
                                        onCopy={newPw.copyGeneratedPassword}
                                        hintKey="passwordReset.page.generatedHint"
                                        copyKey="passwordReset.page.copy"
                                        copiedKey="passwordReset.page.copied"
                                    />
                                </div>
                            )}
                            <div className="field col-span-12 mt-6">
                                <Button
                                    id="resetPasswordSubmitBtn"
                                    className="w-full"
                                    icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-check"}
                                    label={t("passwordReset.page.submit")}
                                    disabled={submitting || !newPassword}
                                />
                            </div>
                            {error && (
                                <div className="field col-span-12">
                                    <Message id="resetPasswordError" severity="error" text={error} className="w-full" />
                                </div>
                            )}
                            <div className="field col-span-12 mt-2 !mb-0 text-right">
                                <a id="resetPasswordToLoginLink" href="/">
                                    {t("passwordReset.page.backToLogin")}
                                </a>
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}
